import { useEffect, useState } from 'react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import AdminProductLinks from 'components/common/admin_product_links';
import ImageGallery from 'components/common/image_gallery';
import Backordered from 'components/pages/product/backordered';
import FinancingModal from 'components/pages/product/financing_modal';
import ProductOptions from 'components/pages/product/product_options';
import HardwareWithSoftwareAddOnOptions from 'components/pages/product/product_with_software_add_on/hardware_with_software_add_on_options';
import ProductBanner from 'components/common/banners/product_banner';
import QrModal from 'components/common/qr_modal';
import PickupOptionsModal from 'typescript/pages/product/bopis/pickup_options_modal';
import {
  STICKY_HEADER_OFFSET,
  onPageView,
} from 'components/pages/product/helpers';
import { checkAddOnsForSoftware } from 'components/pages/product/product_with_software_add_on/utils';
import { BASE_PRODUCT_KEYS } from 'constants/products';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';

const useStyles = makeStyles((theme) => ({
  stickyGalleryContainer: {
    position: 'sticky',
    alignSelf: 'flex-start',
    // TODO(aiven@): this is the height of the sticky CTA subnav, figure out
    // a better way to do this...
    top: `${STICKY_HEADER_OFFSET}px`,
  },
  galleryRoot: {
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      borderBottomRightRadius: '11px',
      borderTopRightRadius: '11px',
    },
  },
  detailsContainer: {
    maxWidth: '500px',
    padding: theme.spacing(0, 4),
    [theme.breakpoints.down('md')]: {
      margin: 'auto',
      padding: theme.spacing(0, 2),
    },
  },
}));

const ProductConfiguration = (props) => {
  const {
    current_user,
    backordered,
    alternatives = [],
    alternatives_metadata = {},
  } = props;

  const { currentProduct, addOns } = useProductContext();

  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [financingModalOpen, setFinancingModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [bopisPickupOptionsModalOpen, setBopisPickupOptionsModalOpen] =
    useState(false);
  const [chosenPickupLocationId, setChosenPickupLocationId] = useState(null);
  const [currentPickupOptions, setCurrentPickupOptions] = useState(
    currentProduct?.shipping?.bopis_stores,
  );
  const [bopisPostalCode, setBopisPostalCode] = useState(
    current_user.request_zip_code,
  );
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState(
    currentProduct?.images?.gallery || [],
  );

  useEffect(() => {
    // Ensure that we have the latest product when swapping alternative products
    setGalleryImages(currentProduct?.images?.gallery || []);
  }, [currentProduct]);

  useEffect(() => {
    if (currentProduct?.tracking_data) {
      onPageView(currentProduct.tracking_data);
    }
  }, [currentProduct]);

  const appendRelationImages = (relatedProductKey) => {
    const defaultImages = currentProduct?.images?.gallery || [];
    const relatedImages = currentProduct?.images?.relation?.[relatedProductKey];

    if (!relatedImages || relatedImages.length === 0) {
      setGalleryImages(defaultImages);
      setActiveImageIndex(0);
      return;
    }

    // the active image index should be set to the first related image
    setGalleryImages([...defaultImages, ...relatedImages]);
    setActiveImageIndex(defaultImages.length);
  };

  const productOptions = () => {
    if (
      checkAddOnsForSoftware(addOns) ||
      // Force for s3b-kiosk. This is the only product that uses this special option
      // and we want this special option to be rendered initially when there is no add on products
      // to start, not the defaulted option.
      currentProduct?.base_product_key === BASE_PRODUCT_KEYS.S3B_KIOSK
    ) {
      return (
        <HardwareWithSoftwareAddOnOptions
          alternatives={alternatives}
          currentAltMetadata={
            alternatives_metadata[currentProduct?.product_key]
          }
          setFinancingModalOpen={setFinancingModalOpen}
        />
      );
    }

    return (
      <ProductOptions
        setFinancingModalOpen={setFinancingModalOpen}
        setBopisPickupOptionsModalOpen={setBopisPickupOptionsModalOpen}
        alternatives={alternatives}
        currentAltMetadata={alternatives_metadata[currentProduct?.product_key]}
        appendRelationImages={appendRelationImages}
        bopisPickupLocation={
          currentPickupOptions?.find(
            (store) => store.location_id === chosenPickupLocationId,
          ) || currentPickupOptions?.[0]
        }
        bopisPostalCode={bopisPostalCode}
      />
    );
  };

  return (
    <>
      <AdminProductLinks product={currentProduct} />
      {currentProduct?.banner && (
        <ProductBanner
          type={currentProduct.banner.type}
          content={currentProduct.banner}
        />
      )}
      <Grid container mt={{ md: 4 }} mb={8}>
        <Grid
          item
          container
          xs={12}
          md={6}
          justifyContent={'center'}
          flexDirection={'column'}
          alignItems={'center'}
          className={mobile ? '' : classes.stickyGalleryContainer}
        >
          <ImageGallery
            images={galleryImages}
            threeSixty={currentProduct?.images?.three_sixty}
            threeDimensionalModel={currentProduct?.three_dimensional_model}
            activeImageIndex={activeImageIndex}
            setActiveImageIndex={setActiveImageIndex}
            aspectRatio={8 / 5}
            transitionDuration={400}
            setQrModalOpen={setQrModalOpen}
            setQrCodeUrl={setQrCodeUrl}
            classes={{ galleryRoot: classes.galleryRoot }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box className={classes.detailsContainer}>
            {backordered ? (
              <Backordered
                currentProduct={currentProduct}
                backordered={backordered}
                setFinancingModalOpen={setFinancingModalOpen}
              />
            ) : (
              productOptions()
            )}
          </Box>
        </Grid>
      </Grid>
      <FinancingModal
        isLoggedIn={current_user.is_logged_in}
        open={financingModalOpen}
        handleClose={() => setFinancingModalOpen(false)}
      />
      <QrModal
        url={qrCodeUrl}
        footer={t('productPage.qrModal.footer')}
        open={qrModalOpen}
        handleClose={() => setQrModalOpen(false)}
      />
      {bopisPickupOptionsModalOpen && (
        <PickupOptionsModal
          handleClose={() => setBopisPickupOptionsModalOpen(false)}
          slug={currentProduct?.slug}
          postalCode={bopisPostalCode}
          setPostalCode={setBopisPostalCode}
          chosenPickupLocationId={chosenPickupLocationId}
          setChosenPickupLocationId={setChosenPickupLocationId}
          currentPickupOptions={currentPickupOptions}
          setCurrentPickupOptions={setCurrentPickupOptions}
        />
      )}
    </>
  );
};

export default ProductConfiguration;
